<template>
  <div>
    <div class="lg:hidden py-4 sticky top-0 z-100 bg-white">
      <TContainer size="xl">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-4">
            <AppHeaderLogo
              :to="localePath({ name: 'index' })"
              size="sm"
            />

            <span class="text-sm text-primary-950 font-medium">{{ t('rvRentalMarketplace') }}</span>
          </div>

          <TButton
            :to="linkListRv"
            size="sm"
            @click="handleHostAdClick"
          >
            {{ t('listYourRv') }}
          </TButton>
        </div>
      </TContainer>
    </div>

    <div
      v-if="data?.destinationDetails?.RouteDetails?.PointOfInterest"
      class="pt-10 pb-12 lg:pt-16 lg:pb-20 space-y-12 lg:space-y-20"
    >
      <div>
        <TContainer size="xl">
          <TBreadcrumb
            v-if="breadcrumbItems?.length"
            :items="breadcrumbItems"
            class="mb-4"
          />

          <THeading
            :level="2"
            class="mb-3"
          >
            {{ t('heroTitle', { location: locationTitle }) }}
          </THeading>

          <TSubHeading>
            <span class="font-bold">{{ t('heroIntroShort', { count: stats?.TotalCampgrounds ?? 0, location: locationTitle }) }}</span>
            {{ t('heroIntro') }}
          </TSubHeading>

          <div
            v-if="stats?.TotalCampgrounds || stats?.TotalCampsites || stats?.TotalRvsAvailableDelivery || stats?.MinNightlyRateLow"
            class="mt-8 flex flex-col gap-8 md:items-center md:flex-row"
          >
            <div
              v-if="stats?.TotalCampgrounds"
              class="font-semibold"
            >
              <TIcon
                :icon="['fal', 'campground']"
                size="xl"
                fixed-width
                class="mr-1"
              />
              {{ t('totalCampgrounds', stats.TotalCampgrounds) }}
            </div>

            <div
              v-if="stats?.TotalCampsites"
              class="font-semibold"
            >
              <TIcon
                :icon="['fal', 'campfire']"
                size="xl"
                fixed-width
                class="mr-1"
              />
              {{ t('campsites', stats.TotalCampsites) }}
            </div>

            <div
              v-if="stats?.TotalRvsAvailableDelivery"
              class="font-semibold"
            >
              <TIcon
                :icon="['fal', 'route']"
                size="xl"
                fixed-width
                class="mr-1"
              />
              {{ t('rvsAvailableForDelivery', stats.TotalRvsAvailableDelivery) }}
            </div>

            <div
              v-if="minNightlyRateDisplay"
              class="font-semibold"
            >
              {{ t('fromPerNight', { amount: minNightlyRateDisplay }) }}
            </div>
          </div>
        </TContainer>
      </div>

      <TContainer size="xl">
        <RvRentalsHostAd
          :max-rv-earnings="rvEarnings?.max"
          :country-code="(countryComponent?.NameAbbreviation as COUNTRY_SHORTS)"
        />
      </TContainer>

      <TContainer
        v-if="campgroundsData?.Data?.length"
        size="xl"
      >
        <div class="mb-6">
          <THeading
            tag="h2"
            :level="3"
          >
            {{ t('campgroundsListTitle', { location: locationTitle }) }}
          </THeading>
        </div>

        <Grid
          :max-results="campgroundsData?.Data?.length ?? pageSize"
          :sm-cols="2"
          :lg-cols="3"
          :md-rows="6"
          :lg-rows="4"
        >
          <template #default="{ getItemClass }">
            <CampgroundsCard
              v-for="(campground, index) in campgroundsData?.Data"
              :key="campground?.Id ?? index"
              :index="index"
              :campground="campground"
              no-lazy-load
              :eager-load="index < EAGER_LOAD_IMAGE_COUNT"
              :class="getItemClass(index)"
            />
          </template>
        </Grid>

        <div class="mt-10 flex justify-center">
          <TButton
            variant="secondary"
            @click="handleViewAllCampgrounds"
          >
            {{ t('viewAllCampgrounds') }}
          </TButton>
        </div>

        <div
          v-if="topAmenities.length"
          class="mt-6"
        >
          <span class="font-semibold">{{ t('mostCommonAmenities') }}</span>
          <div class="flex gap-2 flex-wrap mt-2">
            <TBadge
              v-for="({ key, label, icon }) in topAmenities"
              :key="key"
            >
              <TIcon
                :icon="icon"
                fixed-width
              />
              {{ label }}
            </TBadge>
          </div>
        </div>
      </TContainer>

      <TContainer
        v-if="contentSections.Overview"
        size="xl"
      >
        <TWell class="bg-primary-50 p-6 rounded-xl">
          <THeading
            v-if="contentSections.Overview?.Title"
            :level="4"
            tag="h2"
            class="mb-3"
          >
            {{ contentSections.Overview.Title }}
          </THeading>
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="whitespace-pre-line wrap-anywhere vhtml-link-muted vhtml-paragraph-spacing"
            v-html="contentSections.Overview.Content"
          />
          <!-- eslint-enable vue/no-v-html -->
        </TWell>
      </TContainer>

      <TContainer
        v-if="contentSections.ThingsToDo"
        size="xl"
      >
        <THeading
          v-if="contentSections.ThingsToDo?.Title"
          :level="3"
          tag="h2"
          class="mb-3"
        >
          {{ contentSections.ThingsToDo.Title }}
        </THeading>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="whitespace-pre-line wrap-anywhere vhtml-link-muted vhtml-paragraph-spacing"
          v-html="contentSections.ThingsToDo.Content"
        />
      <!-- eslint-enable vue/no-v-html -->
      </TContainer>

      <div>
        <TContainer size="xl">
          <THeading
            :level="3"
            tag="h2"
            class="mb-4"
          >
            {{ t('campgroundsMaptitle', { location: locationTitle }) }}
          </THeading>

          <div class="mb-8 max-w-2xl">
            <SearchFormMobileTrigger
              is-campgrounds
              class="lg:hidden"
            />
            <SearchFormBar
              is-campgrounds
              class="max-lg:hidden"
            />
          </div>
        </TContainer>

        <TContainer
          v-if="campgroundsData?.Data?.length"
          size="2xl"
        >
          <RvRentalsMap
            :center="mapCenter"
            :bounds="mapBounds"
            :zoom="10"
            :campgrounds="campgroundsData?.Data ?? []"
          />
        </TContainer>
      </div>

      <TContainer
        v-if="contentSections.VideoEmbed?.VideoSrc"
        size="sm"
      >
        <THeading
          v-if="contentSections.VideoEmbed?.Title"
          :level="4"
          tag="h3"
          class="mb-3"
        >
          {{ contentSections.VideoEmbed.Title }}
        </THeading>

        <div class="overflow-hidden rounded-2xl shadow-2xl">
          <iframe
            :src="contentSections.VideoEmbed.VideoSrc"
            :title="contentSections.VideoEmbed?.Title ?? ''"
            allowfullscreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            class="w-full aspect-video block"
          />
        </div>
      </TContainer>

      <TContainer
        v-if="contentSections.SeasonalInfo"
        size="xl"
      >
        <THeading
          v-if="contentSections.SeasonalInfo?.Title"
          :level="3"
          tag="h2"
          class="mb-3"
        >
          {{ contentSections.SeasonalInfo.Title }}
        </THeading>

        <RvRentalsWeatherSeasonality
          v-if="contentSections?.WeatherInfo?.Weather?.length"
          :seasonal-weather="contentSections.WeatherInfo.Weather"
          :use-celsius="useCelsius"
          class="mb-6"
        />

        <!-- eslint-disable vue/no-v-html -->
        <div
          class="whitespace-pre-line wrap-anywhere vhtml-link-muted vhtml-paragraph-spacing"
          v-html="contentSections.SeasonalInfo.Content"
        />
      <!-- eslint-enable vue/no-v-html -->
      </TContainer>

      <TContainer
        v-if="data?.topHosts?.length && data.topHosts.length >= 2"
        size="xl"
        class="my-24"
      >
        <LazyRvRentalsTopHostsSection
          :hosts="data.topHosts"
          :location-title="locationTitle"
        />
      </TContainer>

      <TContainer
        v-if="contentSections.BookingHowTo"
        size="xl"
      >
        <TWell class="bg-primary-50 p-6 rounded-xl">
          <THeading
            v-if="contentSections.BookingHowTo?.Title"
            :level="4"
            tag="h2"
            class="mb-3"
          >
            {{ contentSections.BookingHowTo.Title }}
          </THeading>
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="whitespace-pre-line wrap-anywhere vhtml-link-muted vhtml-paragraph-spacing"
            v-html="contentSections.BookingHowTo.Content"
          />
          <!-- eslint-enable vue/no-v-html -->
        </TWell>
      </TContainer>

      <TContainer
        v-if="contentSections.WhatToExpect"
        size="xl"
      >
        <THeading
          v-if="contentSections.WhatToExpect?.Title"
          :level="3"
          tag="h2"
          class="mb-3"
        >
          {{ contentSections.WhatToExpect.Title }}
        </THeading>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="whitespace-pre-line wrap-anywhere vhtml-link-muted vhtml-paragraph-spacing"
          v-html="contentSections.WhatToExpect.Content"
        />
      <!-- eslint-enable vue/no-v-html -->
      </TContainer>

      <TContainer size="xl">
        <RvRentalsHostAd
          :max-rv-earnings="rvEarnings?.max"
          :country-code="(countryComponent?.NameAbbreviation as COUNTRY_SHORTS)"
        />
      </TContainer>

      <!-- FAQ section -->
      <TContainer
        v-if="faqs?.length"
        size="xl"
      >
        <THeading
          tag="h2"
          :level="3"
          class="mb-6"
        >
          {{ t('faqTitle', { location: locationTitle }) }}
        </THeading>

        <TFaq :faqs="faqs" />
      </TContainer>

      <!-- Nearby destinations -->
      <TContainer
        v-if="data?.destinationDetails?.Nearby?.Results?.length"
        size="xl"
      >
        <THeading
          tag="h2"
          :level="3"
          class="mb-6"
        >
          {{ nearbyTitle }}
        </THeading>

        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
          <NuxtLink
            v-for="location in data.destinationDetails?.Nearby?.Results"
            :key="location.Id"
            :to="localePath({
              name: ROUTE_BASE_NAME.campgrounds_destination,
              params: {
                destinationPath: destinationPathSegments(location.CanonicalPath),
              },
            })"
            class="text-sm text-primary-950 hover:underline truncate"
          >
            {{ location.ParentAttributes?.Abbreviation ? `${location.Name}, ${location.ParentAttributes.Abbreviation}` : location.Name }}
          </NuxtLink>
        </div>
      </TContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ROUTE_BASE_NAME } from '~/constants'
import { POINT_OF_INTEREST_CAMPGROUNDS_CONTENT_KEYWORD, POINT_OF_INTEREST_TYPE } from '~/constants/point-of-interest'
import { AMENITY_ICONS, AMENITY_LABELS } from '~/constants/campground'

import type { CampgroundSearchQuery, PointOfInterestContentData } from '~/types/rental-api-aliases'
import type { COUNTRY_SHORTS } from '~/constants'
import type { SupportedLocales } from '#shared/types/locales'
import type { Breadcrumb } from '~/components/_tailwind/breadcrumb/types'
import type { CampgroundAmenity } from '~/types/campground'

definePageMeta({
  middleware: [
    'destination-path',
  ],
  layout: 'static',
  scrollToTop: true,
  headerSearchFormVisibility: 'hidden',

  i18n: {
    paths: {
      en: '/campgrounds/destination/[...destinationPath]',
      fr: '/campings/destination/[...destinationPath]',
    },
  },
})

const { locale, availableLocales, te, t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { $gis, $captureError, $rentals } = useNuxtApp()
const { linkListRv } = useOwnerCommon()
const { priceDisplay } = usePrice()

const { getDestinationDetails, getComponentByKey } = usePointOfInterest()

const requestKey = computed(() => `campgrounds-destination-${Object.values(route.params).flat().join('-')}-${locale.value}`)

const { data, error } = await useAsyncData(requestKey, async () => {
  const destinationDetails = await getDestinationDetails({ action: 'campgrounds', blockContentKeyword: POINT_OF_INTEREST_CAMPGROUNDS_CONTENT_KEYWORD })

  if (!destinationDetails?.RouteDetails?.PointOfInterest) {
    return { destinationDetails }
  }

  // Redirect to canonical path by locale
  const destinationPath = destinationPathString(route.params.destinationPath)
  const localizedCanonicalPath = destinationDetails.RouteDetails.PointOfInterest?.CanonicalPaths?.[locale.value]
  if (localizedCanonicalPath && localizedCanonicalPath !== destinationPath) {
    const newPath = localePath({
      name: ROUTE_BASE_NAME.campgrounds_destination,
      params: {
        destinationPath: destinationPathSegments(localizedCanonicalPath),
      },
    })
    await navigateTo(newPath, { redirectCode: 301 })

    return { destinationDetails }
  }

  const country = getComponentByKey(destinationDetails.RouteDetails.Breadcrumbs, 'Country')
  const isCountry = destinationDetails.RouteDetails.PointOfInterest.Component === POINT_OF_INTEREST_TYPE.Country
  const isCity = destinationDetails.RouteDetails.PointOfInterest.Component === POINT_OF_INTEREST_TYPE.City
  const region = getComponentByKey(destinationDetails.RouteDetails.Breadcrumbs, 'Region')
  const language = locale.value === 'fr' ? 'Fr' : 'En'
  const googlePlaceId = destinationDetails.RouteDetails.PointOfInterest.Attributes?.GooglePlaceId as string | undefined

  const gisPromise = googlePlaceId
    ? $gis('/Place/Details', {
        query: {
          PlaceId: googlePlaceId,
          Language: language,
        },
      })
    : $gis('/Geocoding/Geocode', {
        query: {
          Address: (() => {
            const address = [destinationDetails.RouteDetails.PointOfInterest.Name]
            const region = getComponentByKey(destinationDetails.RouteDetails.Breadcrumbs, 'Region')
            if (region?.Names?.[locale.value]) {
              address.push(region.Names[locale.value])
            }
            if (country?.Names?.[locale.value]) {
              address.push(country.Names[locale.value])
            }
            return address.filter(Boolean).join(', ')
          })(),
          Language: language,
        },
      })

  const topHostsPromise = $rentals('/api/hosts/top-hosts', {
    query: {
      Latitude: isCity ? destinationDetails.RouteDetails.PointOfInterest.Coordinates?.Latitude : undefined,
      Longitude: isCity ? destinationDetails.RouteDetails.PointOfInterest.Coordinates?.Longitude : undefined,
      SearchRegion: isCountry ? undefined : region?.Names?.en ?? undefined,
      RadiusKm: isCity ? 50 : undefined,
      Limit: 2,
    },
  })

  const [gisData, topHosts] = await Promise.all([gisPromise, topHostsPromise])

  return {
    destinationDetails,
    gisData,
    topHosts: topHosts ?? [],
  }
},
)

if (error.value) {
  $captureError(error.value)
  throw createError({ ...error.value, fatal: true })
}

if (!data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.CanonicalPaths?.[locale.value]) {
  throw createError({
    status: 404,
    statusText: 'Not Found',
    fatal: true,
  })
}

const regionComponent = computed(() => {
  return getComponentByKey(data.value?.destinationDetails?.RouteDetails?.Breadcrumbs, 'Region')
})

const countryComponent = computed(() => {
  return getComponentByKey(data.value?.destinationDetails?.RouteDetails?.Breadcrumbs, 'Country')
})

const stats = computed(() => data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Stats?.CampgroundStats)

const minNightlyRateDisplay = computed(() => {
  const rate = stats.value?.MinNightlyRateLow
  if (!rate) {
    return ''
  }
  const countryCode = countryComponent.value?.NameAbbreviation as COUNTRY_SHORTS | undefined

  return priceDisplay({ value: rate, countryCode, round: true })
})

function getAmenityLabel(amenity: CampgroundAmenity) {
  if (!AMENITY_LABELS[amenity]) {
    return amenity
  }

  const translationKey = `campgroundAmenities.${AMENITY_LABELS[amenity]}`
  return te(translationKey) ? t(translationKey) : amenity
}

const topAmenities = computed(() => Object.entries(stats.value?.AmenityCounts ?? {})
  .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
  .filter((entry): entry is [CampgroundAmenity, number] => entry[0] in AMENITY_ICONS)
  .slice(0, 10)
  .map(([amenity]) => ({
    key: amenity,
    label: getAmenityLabel(amenity),
    icon: AMENITY_ICONS[amenity]!,
  })))

const pageSize = 12

const isCity = computed(() => data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Component === POINT_OF_INTEREST_TYPE.City)
const isRegion = computed(() => data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Component === POINT_OF_INTEREST_TYPE.Region)

const mapBounds = computed(() => {
  if (isCity.value) {
    return undefined
  }

  const bounds = data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Bounds
  if (!bounds) {
    return undefined
  }
  return { north: bounds.North, south: bounds.South, east: bounds.East, west: bounds.West }
})

const mapCenter = computed(() => {
  const coordinates = data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Coordinates
  if (!coordinates) {
    return undefined
  }
  return { lat: coordinates.Latitude, lng: coordinates.Longitude }
})

const campgroundsDestinationQuery = computed((): CampgroundSearchQuery => ({
  Language: lookupLocaleToLanguage(locale.value),
  Country: countryComponent.value?.NameAbbreviation as COUNTRY_SHORTS,
  PageSize: pageSize,
  SortBy: isCity.value ? 'DistanceAsc' : undefined,
  Latitude: isCity.value ? data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Coordinates?.Latitude : undefined,
  Longitude: isCity.value ? data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Coordinates?.Longitude : undefined,
  Region: isRegion.value ? regionComponent.value?.NameAbbreviation ?? undefined : undefined,
}))

const { data: campgroundsData } = await useRentals('/api/campgrounds', {
  query: campgroundsDestinationQuery,
})

const useCelsius = computed(() => {
  const countryCode = countryComponent.value?.NameAbbreviation
  return !countryCode || countryCode === 'CA'
})

const locationTitle = computed(() => data.value?.destinationDetails?.RouteDetails?.PointOfInterest
  ? pointOfInterestLocationNameWithPreposition({
      locale: locale.value,
      pointOfInterestId: data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Id,
      name: data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Name,
      component: data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Component,
      parentName: typeof data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.ParentAttributes?.Abbreviation === 'string' ? data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.ParentAttributes?.Abbreviation : data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.ParentName,
      grammarRules: data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Attributes?.GrammarRules,
      useNearby: true,
    })
  : '')

const pageTitle = computed(() => t('pageTitle', { location: locationTitle.value, pageTitleCount: stats.value?.TotalCampgrounds ? t('pageTitleCount', { count: stats.value.TotalCampgrounds }) : '' }))

useSeoHeadTags({
  title: pageTitle,
  description: computed(() => t('pageDescription', { location: locationTitle.value })),
})

const { getDynamicImage, presets } = useImage()

const EAGER_LOAD_IMAGE_COUNT = 3
useHead({
  link: (campgroundsData.value?.Data ?? []).slice(0, EAGER_LOAD_IMAGE_COUNT).flatMap((campground) => {
    const path = campground.Photos?.[0]
    if (!path) {
      return []
    }

    const href = getDynamicImage({ path, preset: presets.LANDSCAPE_SM })
    if (!href) {
      return []
    }

    return [{
      rel: 'preload',
      as: 'image',
      fetchpriority: 'high',
      href,
    }]
  }),
})

// Breadcrumbs
const breadcrumbItems = computed<Breadcrumb[]>(() => {
  const items: Breadcrumb[] = [{
    text: t('campgrounds'),
    to: localePath({ name: ROUTE_BASE_NAME.campgrounds }),
  }]

  data.value?.destinationDetails?.RouteDetails?.Breadcrumbs?.forEach((x) => {
    const canonicalPath = x.CanonicalPaths?.[locale.value]
    const name = x.Names?.[locale.value]
    if (!name) return
    items.push({
      text: name,
      to: localePath({
        name: ROUTE_BASE_NAME.campgrounds_destination,
        params: {
          destinationPath: destinationPathSegments(canonicalPath),
        },
      }),
    })
  })
  return items
})

const faqs = computed(() => data.value?.destinationDetails?.RouteDetails?.FAQs?.filter((faq) => Boolean(faq.Question) && Boolean(faq.Answer))?.map((faq) => ({
  question: faq.Question ?? '',
  answer: faq.Answer ?? '',
  source: faq.Source ?? undefined,
})) ?? [])

// Typed editorial sections (Overview, ThingsToDo, SeasonalInfo, BookingHowTo, WhatToExpect, ...).
// Backend returns exactly one block per POI/language for the `campgrounds-destination` slug;
// the typed list lives in that block's `ContentJson`.
const contentSections = computed(() => {
  const blocks = data.value?.destinationDetails?.ContentBlocks?.[0]?.ContentJson?.filter(
    (block) => Boolean(block.Content || block.Title || block.Weather?.length || block.VideoSrc),
  ) ?? []

  return blocks.reduce<Record<string, PointOfInterestContentData>>((acc, block) => {
    const key = block.ContentType ?? 'Prose'
    acc[key] = block
    return acc
  }, {})
})

// Lang switcher
const setI18nParams = useSetI18nParams()
const i18nParams: Record<SupportedLocales, Record<'destinationPath', string[]> | undefined> = { en: undefined, fr: undefined }
availableLocales.forEach((localeCode) => {
  const canonicalPath = data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.CanonicalPaths?.[localeCode]
  i18nParams[localeCode] = {
    destinationPath: destinationPathSegments(canonicalPath),
  }
})
setI18nParams(i18nParams)

onBeforeRouteLeave(() => {
  setI18nParams({})
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        text: t('pageLink.home'),
        to: localePath({ name: 'index' }),
      },
      ...breadcrumbItems.value,
    ].map(({ text, to }) => ({
      name: text,
      item: to,
    })),
  }),
  defineWebPage({
    '@type': 'CollectionPage',
    'name': pageTitle.value,
    'description': t('pageDescription', { location: locationTitle.value }),
  }),
])

const { logCustomEvent } = await useBraze()
const rtsessionIdCookie = useRtsessionIdCookie()

const rvEarningsQuery = computed(() => ({
  country: countryComponent.value?.NameAbbreviation,
  region: regionComponent.value?.NameAbbreviation,
}))

const { data: rvEarnings } = await useFetch('/api/rv-earnings', {
  query: rvEarningsQuery,
})

function logDestinationPageViewed(poi: typeof data.value) {
  const pointOfInterest = poi?.destinationDetails?.RouteDetails?.PointOfInterest

  const countryCode = (countryComponent.value?.NameAbbreviation ?? 'CA') as COUNTRY_SHORTS
  const currency = countryCode ? lookupCountryCodeToCurrencyCode(countryCode) : 'CAD'

  // Don't fire the Braze event when earnings data is unavailable or the API returned
  // zero/empty values — sending empty amounts produces misleading in-app messages.
  if (!rvEarnings.value?.min || !rvEarnings.value?.max) return

  logCustomEvent('InApp_CampgroundDestinationPageViewed', {
    destinationName: locationTitle.value,
    currency,
    minAmount: priceDisplay({ value: rvEarnings.value.min, countryCode, round: true }),
    maxAmount: priceDisplay({ value: rvEarnings.value.max, countryCode, round: true }),
    cta: linkListRv.value,
    language: locale.value,
    sessionId: rtsessionIdCookie.value,
  })

  // Mark this POI as logged only after the event actually fires so that a skipped
  // earnings-guard on onMounted doesn't prevent the rvEarnings watcher from retrying.
  lastLoggedPoiId.value = pointOfInterest?.Id
}

// Starts as undefined so the rvEarnings watcher can retry for the initial POI if
// onMounted returned early due to empty earnings.
const lastLoggedPoiId = ref<string | undefined>(undefined)

onMounted(() => logDestinationPageViewed(data.value))

// Watch rvEarnings instead of data so the event fires only after the new
// /api/rv-earnings response arrives for the destination, not before it.
watch(rvEarnings, () => {
  const currentPoiId = data.value?.destinationDetails?.RouteDetails?.PointOfInterest?.Id
  if (!currentPoiId || currentPoiId === lastLoggedPoiId.value) return

  logDestinationPageViewed(data.value)
})

const { navigateToCampgroundsSearch, searchData } = useSearch()
if (data.value?.gisData) {
  searchData.value.location = data.value.gisData
}
async function handleViewAllCampgrounds() {
  await navigateToCampgroundsSearch({ PlaceId: data.value?.gisData?.placeId ?? undefined })
}

const nearbyTitle = computed(() => {
  if (isCity.value) {
    return t('nearbyCitiesTitle')
  }
  else if (isRegion.value) {
    return t('nearbyRegionsTitle', { location: locationTitle.value })
  }
  else if (countryComponent.value?.NameAbbreviation === 'CA') {
    return t('nearbyTitleCA')
  }

  return t('nearbyTitleUS')
})

const { pageSource } = usePageSource()
function handleHostAdClick() {
  trackCTAClicked({
    cta: t('listYourRv'),
    pageSource: pageSource.value,
  })
}
</script>

<i18n src="~~/i18n/locales/common/campground.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "pageTitle": "Best campgrounds and RV Parks {location}{pageTitleCount}",
    "pageTitleCount": " ({count}+ campgrounds)",
    "pageDescription": "Discover the perfect RV campground {location}. Browse RV parks, compare amenities, read reviews, and book your perfect camping spot.",
    "rvRentalMarketplace": "RV rental marketplace",
    "heroTitle": "Campgrounds and RV parks {location}",
    "heroIntroShort": "{count} campgrounds and RV Parks {location}.",
    "heroIntro": "Compare campsite amenities, photos, and site types to find the perfect stop for your rig on your route.",
    "totalCampgrounds": "0 campgrounds | 1 campground | {n} campgrounds",
    "campsites": "0 campsites | 1 campsite | {n} campsites",
    "rvsAvailableForDelivery": "0 RVs available for delivery | 1 RV available for delivery | {n} RVs available for delivery",
    "fromPerNight": "From {amount}/night",
    "mostCommonAmenities": "Most common amenities",
    "campgroundsListTitle": "Best campgrounds {location}",
    "viewAllCampgrounds": "View all campgrounds",
    "campgroundsMaptitle": "Camping spots and RV parks {location}",
    "nearbyCitiesTitle": "Nearby cities with campgrounds",
    "nearbyRegionsTitle": "Cities {location} with campgrounds",
    "nearbyTitleUS": "States with campgrounds",
    "nearbyTitleCA": "Provinces with campgrounds",
    "campgrounds": "Campgrounds",
    "faqTitle": "Frequently asked questions about campgrounds {location}",
    "listYourRv": "List your RV"
  },
  "fr": {
    "pageTitle": "Meilleurs campings et parcs pour VR {location}{pageTitleCount}",
    "pageTitleCount": " ({count}+ campings)",
    "pageDescription": "Trouvez les meilleurs terrains de camping {location}. Parcourez les parcs VR, comparez les commodités, lisez les avis et réservez votre camping idéal.",
    "rvRentalMarketplace": "Plateforme de location de VRs",
    "heroTitle": "Terrains de camping et parcs pour VR {location}",
    "heroIntroShort": "{count} campings et parcs VR {location}.",
    "heroIntro": "Comparez les commodités, les photos et les types d'emplacements pour trouver l'arrêt parfait pour votre VR.",
    "totalCampgrounds": "0 campings | 1 camping | {n} campings",
    "campsites": "0 site | 1 site | {n} sites",
    "rvsAvailableForDelivery": "0 VR disponibles pour la livraison | 1 VR disponible pour la livraison | {n} VRs disponibles pour la livraison",
    "fromPerNight": "À partir de {amount}/nuit",
    "mostCommonAmenities": "Commodités les plus populaires",
    "campgroundsListTitle": "Meilleurs campings {location}",
    "viewAllCampgrounds": "Voir tous les campings",
    "campgroundsMaptitle": "Campings et parcs VR {location}",
    "nearbyCitiesTitle": "Villes à proximité avec campings",
    "nearbyRegionsTitle": "Villes {location} avec campings",
    "nearbyTitleUS": "États avec campings",
    "nearbyTitleCA": "Provinces avec campings",
    "campgrounds": "Campings",
    "faqTitle": "Questions fréquentes sur les campings {location}",
    "listYourRv": "Inscrire votre VR"
  }
}
</i18n>
